import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Id } from '../../api/types';
import { PROJECT_PARAM } from '../../components/layout/nav-links';

/**
 * The board's state lives in the URL, for the same reason the backlog's does: "the APFS board with
 * Development expanded" is a thing one person sends another, and Back out of a ticket has to return
 * to the board you were looking at rather than to a default one.
 *
 * ⭐ The lane a phone is currently showing is in there too — a reload on a phone must not silently
 * jump you back to Intake while you were reading Production.
 */
export function useBoardParams() {
  const [params, setParams] = useSearchParams();

  /**
   * ⭐ THE SHELL'S KEY, imported rather than retyped. The project rail, the top-bar switcher and this
   * page are three controls over ONE piece of state; a private `?p=` here would let the rail
   * highlight APFS while the board drew TBS, and each would look correct on its own.
   * ⛔ Which also means this page does not SET it — the rail does.
   */
  const projectId = params.get(PROJECT_PARAM);
  const selectedId = params.get('ticket');
  const lane = params.get('lane');

  const expandedLanes = useMemo(() => {
    const raw = params.get('expand');
    if (!raw) return new Set<string>();
    // ⚠️ Lane codes are vocabulary values, so they are validated by SHAPE only; an unknown code
    // simply expands nothing, which is what an old link to a retired lane should do.
    return new Set(raw.split(',').filter((code) => /^[a-z0-9_]{1,32}$/i.test(code)));
  }, [params]);

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void, options?: { replace?: boolean }) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: options?.replace ?? false });
    },
    [params, setParams],
  );

  /** ⭐ `replace` — expanding a lane changes the VIEW, not the place. Back should leave the board. */
  const toggleLane = useCallback(
    (code: string) => {
      write(
        (next) => {
          const current = new Set((next.get('expand') ?? '').split(',').filter(Boolean));
          if (current.has(code)) current.delete(code);
          else current.add(code);
          setOrDelete(next, 'expand', [...current].join(','));
        },
        { replace: true },
      );
    },
    [write],
  );

  const setLane = useCallback(
    (code: string | null) => {
      write((next) => setOrDelete(next, 'lane', code), { replace: true });
    },
    [write],
  );

  const select = useCallback(
    (ticketId: Id | null) => {
      write((next) => setOrDelete(next, 'ticket', ticketId));
    },
    [write],
  );

  return { projectId, selectedId, lane, expandedLanes, toggleLane, setLane, select };
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null | undefined): void {
  if (value === null || value === undefined || value === '') params.delete(key);
  else params.set(key, value);
}
